'use client';

import { useEffect } from 'react';
import { useSeqLog } from '@/hooks/telemetry/use-seq-log';

const FOCUS_SCROLL_DEBUG_COMPONENT = 'FocusScrollDebug';
const FOCUS_SCROLL_DEBUG_FEATURE = 'focus-scroll';
const SNAPSHOT_OFFSETS_MS = [50, 150, 300, 600, 1000, 1500, 2500] as const;
const DEBUG_SESSION_DURATION_MS = 3000;
const ANCESTOR_CHAIN_MAX_DEPTH = 12;
const DATA_NAME_ATTRIBUTE = 'data-name';
const FOCUS_SESSION_ID_PREFIX = 'focus';
const SNAPSHOT_EVENT_PREFIX = 'snapshot-+';
const SNAPSHOT_NOTE_PREFIX = '+';
const SNAPSHOT_MS_SUFFIX = 'ms';
const EMPTY_VISUAL_VIEWPORT_SNAPSHOT = {
  vvHeight: null,
  vvWidth: null,
  vvOffsetTop: null,
  vvOffsetLeft: null,
  vvPageTop: null,
  vvScale: null,
  inputBottomBelowVisualBottom: null,
};

enum FocusScrollDebugEvent {
  Focus = 'focus-event',
  VisualViewportResize = 'visualviewport-resize',
  VisualViewportScroll = 'visualviewport-scroll',
  WindowScroll = 'window-scroll',
  SessionEnd = 'session-end',
}

enum FocusScrollDebugNote {
  FocusIn = 'focusin',
  VisualViewportResize = 'vv-resize',
  VisualViewportScroll = 'vv-scroll',
  WindowScroll = 'window-scroll',
  TimeoutEnd = 'timeout-end',
  FocusOutEnd = 'focusout-end',
}

interface AncestorInfo {
  tag: string;
  dataName: string | null;
  overflowY: string;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  scrollSnapType: string;
  scrollSnapAlign: string;
  scrollPaddingBottom: string;
}

interface FocusSnapshot extends Record<string, unknown> {
  note: string;
  sessionId: string;
  elapsedMs: number;
  pathname: string;
  userAgent: string;
  inputTag: string;
  inputType: string | null;
  inputId: string | null;
  inputName: string | null;
  inputDataName: string | null;
  inputContentEditable: boolean;
  inputRectTop: number;
  inputRectBottom: number;
  inputRectHeight: number;
  inputRectLeft: number;
  windowInnerHeight: number;
  windowInnerWidth: number;
  windowScrollY: number;
  windowScrollX: number;
  docElScrollTop: number;
  docElClientHeight: number;
  docElScrollHeight: number;
  scrollingElementScrollTop: number | null;
  vvHeight: number | null;
  vvWidth: number | null;
  vvOffsetTop: number | null;
  vvOffsetLeft: number | null;
  vvPageTop: number | null;
  vvScale: number | null;
  isInputActive: boolean;
  inputBottomBelowVisualBottom: number | null;
  ancestorChain: AncestorInfo[];
}

function isEditableElement(node: EventTarget | null): node is HTMLElement {
  if (!(node instanceof HTMLElement)) return false;
  if (node instanceof HTMLInputElement) return true;
  if (node instanceof HTMLTextAreaElement) return true;
  return node.isContentEditable;
}

function buildAncestorChain(input: HTMLElement): AncestorInfo[] {
  const chain: AncestorInfo[] = [];
  let cursor: Element | null = input.parentElement;
  while (cursor && chain.length < ANCESTOR_CHAIN_MAX_DEPTH) {
    const cs = window.getComputedStyle(cursor);
    chain.push({
      tag: cursor.tagName,
      dataName: cursor.getAttribute(DATA_NAME_ATTRIBUTE),
      overflowY: cs.overflowY,
      scrollTop: cursor.scrollTop,
      scrollHeight: cursor.scrollHeight,
      clientHeight: cursor.clientHeight,
      scrollSnapType: cs.scrollSnapType,
      scrollSnapAlign: cs.scrollSnapAlign,
      scrollPaddingBottom: cs.scrollPaddingBottom,
    });
    cursor = cursor.parentElement;
  }
  return chain;
}

function getInputName(input: HTMLElement): string | null {
  if (input instanceof HTMLInputElement) return input.name || null;
  if (input instanceof HTMLTextAreaElement) return input.name || null;
  return null;
}

function buildInputSnapshot(input: HTMLElement, rect: DOMRect) {
  return {
    inputTag: input.tagName,
    inputType: input instanceof HTMLInputElement ? input.type : null,
    inputId: input.id || null,
    inputName: getInputName(input),
    inputDataName: input.getAttribute(DATA_NAME_ATTRIBUTE),
    inputContentEditable: input.isContentEditable,
    inputRectTop: rect.top,
    inputRectBottom: rect.bottom,
    inputRectHeight: rect.height,
    inputRectLeft: rect.left,
    isInputActive: document.activeElement === input,
  };
}

function buildWindowSnapshot() {
  return {
    windowInnerHeight: window.innerHeight,
    windowInnerWidth: window.innerWidth,
    windowScrollY: window.scrollY,
    windowScrollX: window.scrollX,
    docElScrollTop: document.documentElement.scrollTop,
    docElClientHeight: document.documentElement.clientHeight,
    docElScrollHeight: document.documentElement.scrollHeight,
    scrollingElementScrollTop: document.scrollingElement?.scrollTop ?? null,
  };
}

function buildVisualViewportSnapshot(rect: DOMRect) {
  const vv = window.visualViewport;
  if (!vv) return EMPTY_VISUAL_VIEWPORT_SNAPSHOT;

  const vvVisualBottom = vv.offsetTop + vv.height;
  return {
    vvHeight: vv.height,
    vvWidth: vv.width,
    vvOffsetTop: vv.offsetTop,
    vvOffsetLeft: vv.offsetLeft,
    vvPageTop: vv.pageTop,
    vvScale: vv.scale,
    inputBottomBelowVisualBottom: rect.bottom - vvVisualBottom,
  };
}

function buildSnapshot({
  input,
  note,
  sessionId,
  startTime,
}: {
  input: HTMLElement;
  note: string;
  sessionId: string;
  startTime: number;
}): FocusSnapshot {
  const rect = input.getBoundingClientRect();
  return {
    note,
    sessionId,
    elapsedMs: Math.round(performance.now() - startTime),
    pathname: window.location.pathname,
    userAgent: navigator.userAgent,
    ...buildInputSnapshot(input, rect),
    ...buildWindowSnapshot(),
    ...buildVisualViewportSnapshot(rect),
    ancestorChain: buildAncestorChain(input),
  };
}

export function useFocusScrollDebug(): void {
  const log = useSeqLog(FOCUS_SCROLL_DEBUG_COMPONENT, { feature: FOCUS_SCROLL_DEBUG_FEATURE });

  useEffect(() => {
    const buildSnapshotEvent = (ms: number): string =>
      `${SNAPSHOT_EVENT_PREFIX}${ms}${SNAPSHOT_MS_SUFFIX}`;
    const buildSnapshotNote = (ms: number): string =>
      `${SNAPSHOT_NOTE_PREFIX}${ms}${SNAPSHOT_MS_SUFFIX}`;

    const handleFocusIn = (event: FocusEvent): void => {
      const input = event.target;
      if (!isEditableElement(input)) return;

      const sessionId = `${FOCUS_SESSION_ID_PREFIX}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const startTime = performance.now();
      let cleaned = false;

      log(FocusScrollDebugEvent.Focus, buildSnapshot({ input, note: FocusScrollDebugNote.FocusIn, sessionId, startTime }));

      const vv = window.visualViewport;

      const onVvResize = (): void => {
        if (cleaned) return;
        log(FocusScrollDebugEvent.VisualViewportResize, buildSnapshot({ input, note: FocusScrollDebugNote.VisualViewportResize, sessionId, startTime }));
      };
      const onVvScroll = (): void => {
        if (cleaned) return;
        log(FocusScrollDebugEvent.VisualViewportScroll, buildSnapshot({ input, note: FocusScrollDebugNote.VisualViewportScroll, sessionId, startTime }));
      };
      const onWindowScroll = (): void => {
        if (cleaned) return;
        log(FocusScrollDebugEvent.WindowScroll, buildSnapshot({ input, note: FocusScrollDebugNote.WindowScroll, sessionId, startTime }));
      };

      vv?.addEventListener('resize', onVvResize);
      vv?.addEventListener('scroll', onVvScroll);
      window.addEventListener('scroll', onWindowScroll, { passive: true, capture: true });

      const snapshotTimers = SNAPSHOT_OFFSETS_MS.map((ms) =>
        window.setTimeout(() => {
          if (cleaned) return;
          log(buildSnapshotEvent(ms), buildSnapshot({ input, note: buildSnapshotNote(ms), sessionId, startTime }));
        }, ms),
      );

      const endSession = (note: string): void => {
        if (cleaned) return;
        cleaned = true;
        snapshotTimers.forEach((id) => clearTimeout(id));
        clearTimeout(cleanupTimer);
        vv?.removeEventListener('resize', onVvResize);
        vv?.removeEventListener('scroll', onVvScroll);
        window.removeEventListener('scroll', onWindowScroll, true);
        document.removeEventListener('focusout', onFocusOut, true);
        log(FocusScrollDebugEvent.SessionEnd, buildSnapshot({ input, note, sessionId, startTime }));
      };

      const cleanupTimer = window.setTimeout(() => endSession(FocusScrollDebugNote.TimeoutEnd), DEBUG_SESSION_DURATION_MS);

      const onFocusOut = (focusOutEvent: FocusEvent): void => {
        if (focusOutEvent.target !== input) return;
        endSession(FocusScrollDebugNote.FocusOutEnd);
      };
      document.addEventListener('focusout', onFocusOut, true);
    };

    document.addEventListener('focusin', handleFocusIn, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [log]);
}
